import { useState } from "react";
import { BellPlus } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { LazyAuthModal as AuthModal } from "@/components/auth/LazyAuthModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createSavedSearch } from "@/lib/saved-searches.functions";
import { describeQuery, type SavedQuery } from "@/lib/saved-search-label";

/** «Bevaka sökningen» – sparar nuvarande filter och notifierar vid nya träffar. */
export function SaveSearchButton({ query }: { query: SavedQuery }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const create = useServerFn(createSavedSearch);
  const [authOpen, setAuthOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [channel, setChannel] = useState<"push" | "email" | "both">("both");

  const summary = describeQuery(query);

  const save = useMutation({
    mutationFn: () =>
      create({
        data: { label: (label.trim() || summary).slice(0, 60), query, notifyChannel: channel },
      }),
    onSuccess: () => {
      setOpen(false);
      setLabel("");
      void qc.invalidateQueries({ queryKey: ["saved-searches"] });
      toast.success("Bevakningen är sparad. Vi hör av oss vid nya träffar.");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Det gick inte just nu."),
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5"
        onClick={() => (user ? setOpen(true) : setAuthOpen(true))}
      >
        <BellPlus className="h-4 w-4" /> Bevaka sökningen
      </Button>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bevaka den här sökningen</DialogTitle>
            <DialogDescription>
              Vi kollar var femtonde minut och hör av oss så fort något nytt matchar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium text-foreground">Det här bevakas</p>
              <p className="mt-0.5 text-muted-foreground">{summary}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="watch-label">Namn på bevakningen</Label>
              <Input
                id="watch-label"
                value={label}
                maxLength={60}
                placeholder={summary.slice(0, 50)}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Hur vill du få notiser?</Label>
              <Select value={channel} onValueChange={(v) => setChannel(v as typeof channel)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Push och mejl</SelectItem>
                  <SelectItem value="push">Bara push</SelectItem>
                  <SelectItem value="email">Bara mejl</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Sparar…" : "Spara bevakning"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
